import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/system";
import DashboardLayout from "../../components/SharedLayouts/DashboardLayout";
import BackArrow2 from "../../components/BackArrow2";
import Loader from "../../components/SharedLayouts/Loader";
import CustomButton from "../../components/Buttons/Button";
import GridTable from "../../components/table/gridTable";
import UpdateStockPriceModal from "../../components/modal/UpdateStockPriceModal";
import ToggleStockActiveModal from "../../components/modal/ToggleStockActiveModal";
import DeleteStockModal from "../../components/modal/DeleteStockModal";
import PlaceOrderForUserModal from "../../components/modal/PlaceOrderForUserModal";
import { getStock } from "../../store/actions/stocks/getStock";
import { getStockPriceHistory } from "../../store/actions/stocks/getStockPriceHistory";
import { getCurrencySymbol } from "../../Helper/getCurrencySymbol";

const DetailItem = ({ label, value }) => (
  <div className="border border-[#EAECF0] rounded-lg p-4">
    <p className="text-xs text-[#475467] mb-1">{label}</p>
    <p className="font-[600] text-base break-words">{value ?? "-"}</p>
  </div>
);

const feeLabels = {
  broker_commission_pct: "Broker Commission %",
  broker_commission_vat_pct: "Broker Commission VAT %",
  sec_fee_pct: "SEC Fee %",
  sec_vat_pct: "SEC VAT %",
  cscs_vat_pct: "CSCS VAT %",
  sms_alert_cscs_fee_flat: "SMS Alert CSCS Fee (flat)",
  sms_alert_cscs_vat_pct: "SMS Alert CSCS VAT %",
  nse_fee_pct: "NSE Fee %",
  cscs_fee_pct: "CSCS Fee %",
  nse_vat_pct: "NSE VAT %",
  stamp_duty_pct: "Stamp Duty %",
};

function StockDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [showUpdatePrice, setShowUpdatePrice] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showPlaceOrder, setShowPlaceOrder] = useState(false);

  const stock = useSelector((state) => state?.stocks?.stock);
  const loading = useSelector((state) => state?.stocks?.getStockPending);
  const priceHistory =
    useSelector((state) => state?.stocks?.priceHistory) || [];
  const priceHistoryPending = useSelector(
    (state) => state?.stocks?.priceHistoryPending,
  );

  useEffect(() => {
    dispatch(getStock(id));
    dispatch(getStockPriceHistory(id));
  }, [dispatch, id]);

  const currencySymbol = getCurrencySymbol(stock?.currency);

  const historyColumns = [
    { field: "id", headerName: "ID", width: 80 },
    {
      field: "price",
      headerName: "Price",
      width: 150,
      renderCell: (params) =>
        params.row?.price != null
          ? `${currencySymbol} ${Number(params.row.price).toFixed(2)}`
          : `${currencySymbol} -`,
    },
    {
      field: "percent_change",
      headerName: "% Change",
      width: 120,
      renderCell: (params) =>
        `${Number(params.row?.percent_change ?? 0).toFixed(2)}%`,
    },
    {
      field: "trend",
      headerName: "Trend",
      width: 110,
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>
          {params.row?.trend || "-"}
        </span>
      ),
    },
    { field: "available_volume", headerName: "Available Volume", width: 160 },
    {
      field: "recorded_at",
      headerName: "Recorded At",
      width: 200,
      renderCell: (params) =>
        params.row?.recorded_at
          ? new Date(params.row.recorded_at).toLocaleString()
          : "-",
    },
  ];

  return (
    <DashboardLayout>
      <div className="px-2 lg:px-8">
        <div className="flex items-center mb-8 flex-row md:items-center">
          <Link to={"/dashboard"}>
            <img src="/Assets/bread.png" alt="" className="w-8 h-8 mr-2" />
          </Link>
          <img src="/Assets/chev.png" alt="" className="w-8 h-8 mr-2" />
          <Link
            to="/stocks"
            className="font-semibold text-[#475467] text-base flex"
          >
            Stocks
          </Link>
          <img src="/Assets/chev.png" alt="" className="w-8 h-8 md:mr-2" />
          <span className="font-semibold text-[#475467] text-base bg-[#F9FAFB] p-1 px-3 rounded-md">
            {stock?.symbol || "Detail"}
          </span>
        </div>

        <BackArrow2 mb={20} ml={0} />

        {loading && !stock ? (
          <Loader />
        ) : (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <div className="flex items-center gap-4">
                {stock?.logo_url && (
                  <img
                    src={stock.logo_url}
                    alt={stock?.symbol}
                    className="w-14 h-14 rounded-full object-contain border border-[#EAECF0]"
                  />
                )}
                <div>
                  <h1 className="font-semibold text-2xl">
                    {stock?.name} ({stock?.symbol})
                  </h1>
                  <p className="text-[#475467] text-sm">
                    {stock?.exchange || "—"} ·{" "}
                    <span
                      style={{
                        color: stock?.is_active ? "#067647" : "#B42318",
                      }}
                    >
                      {stock?.is_active ? "Active" : "Inactive"}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <CustomButton
                  variant="outlined"
                  customColor="#fff"
                  children="Edit Stock"
                  width="120px"
                  height="42px"
                  borderColor="#D0D5DD"
                  color="#344054"
                  onClick={() => navigate(`/stocks/edit/${id}`)}
                />
                <CustomButton
                  variant="contained"
                  customColor="#159AA8"
                  children="Invest for User"
                  width="150px"
                  height="42px"
                  color="#fff"
                  onClick={() => setShowPlaceOrder(true)}
                />
                <CustomButton
                  variant="contained"
                  customColor="#159AA8"
                  children="Update Price"
                  width="140px"
                  height="42px"
                  color="#fff"
                  onClick={() => setShowUpdatePrice(true)}
                />
                <CustomButton
                  variant="contained"
                  customColor={stock?.is_active ? "#B42318" : "#067647"}
                  children={stock?.is_active ? "Deactivate" : "Activate"}
                  width="130px"
                  height="42px"
                  color="#fff"
                  onClick={() => setShowToggle(true)}
                />
                <CustomButton
                  variant="outlined"
                  customColor="#fff"
                  children="Delete Stock"
                  width="130px"
                  height="42px"
                  borderColor="#FDA29B"
                  color="#B42318"
                  onClick={() => setShowDelete(true)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <DetailItem
                label="Current Price"
                value={`${currencySymbol} ${stock?.current_price ?? "-"}`}
              />
              <DetailItem
                label="Previous Close"
                value={
                  stock?.previous_close != null
                    ? `${currencySymbol} ${stock.previous_close}`
                    : "-"
                }
              />
              <DetailItem
                label="% Change"
                value={`${stock?.percent_change ?? 0}%`}
              />
              <DetailItem label="Trend" value={stock?.trend} />
              <DetailItem
                label="Available Volume"
                value={stock?.available_volume}
              />
              <DetailItem label="Minimum Volume" value={stock?.min_volume} />
              <DetailItem label="Currency" value={stock?.currency} />
              <DetailItem label="Sector" value={stock?.sector} />
              <DetailItem
                label="Shariah Compliant"
                value={stock?.is_shariah_compliant ? "Yes" : "No"}
              />
              <DetailItem label="Exchange" value={stock?.exchange} />
            </div>

            {stock?.description && (
              <div className="mb-8">
                <h2 className="font-[600] text-base mb-2">Description</h2>
                <p className="text-[#475467] text-sm border border-[#EAECF0] rounded-lg p-4">
                  {stock.description}
                </p>
              </div>
            )}

            {stock?.fees && (
              <div className="mb-8">
                <h2 className="font-[600] text-base mb-3">Fees</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.keys(feeLabels).map((key) => (
                    <DetailItem
                      key={key}
                      label={feeLabels[key]}
                      value={stock.fees?.[key]}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="mb-8">
              <h2 className="font-[600] text-base mb-3">Price History</h2>
              {priceHistoryPending && priceHistory?.length < 1 ? (
                <Loader />
              ) : (
                <Box sx={{ overflowX: "auto", width: "100%" }}>
                  <GridTable
                    rows={priceHistory}
                    columns={historyColumns}
                    type={"stock-price-history"}
                    noReport
                    noSearch
                  />
                </Box>
              )}
            </div>
          </>
        )}
      </div>

      <UpdateStockPriceModal
        open={showUpdatePrice}
        stock={stock}
        onClose={() => setShowUpdatePrice(false)}
      />
      <ToggleStockActiveModal
        open={showToggle}
        stock={stock}
        onClose={() => setShowToggle(false)}
      />
      <DeleteStockModal
        open={showDelete}
        stock={stock}
        onClose={() => setShowDelete(false)}
      />
      <PlaceOrderForUserModal
        open={showPlaceOrder}
        stock={stock}
        onClose={() => setShowPlaceOrder(false)}
      />
    </DashboardLayout>
  );
}

export default StockDetail;
